import React, { useState, useRef, useEffect } from 'react';

import styles from './CodeField.scss';

type Props = {
  value: string;
  onChange: (value: string) => void;
  cellCount?: number;
  disabled?: boolean;
};

export const CodeField = ({
  value,
  onChange,
  cellCount = 6,
  disabled = false,
}: Props) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const backspacePressedRef = useRef<boolean>(false);

  // Auto-focus first input when component mounts
  useEffect(() => {
    if (inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);

  // Auto-blur when all cells are filled
  useEffect(() => {
    if (
      value.length === cellCount &&
      inputRefs.current &&
      inputRefs.current[cellCount - 1]
    ) {
      inputRefs.current[cellCount - 1]?.blur();
    }
  }, [value.length, cellCount]);

  const handleInputChange = (index: number, inputValue: string) => {
    // Only allow single digit
    const digit = inputValue.replace(/\D/g, '').slice(0, 1);
    
    if (digit) {
      // Update the value
      const newValue = value.split('');

      newValue[index] = digit;
      const updatedValue = newValue.join('').slice(0, cellCount);

      onChange(updatedValue);

      // Move to next input
      if (index < cellCount - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Reset backspace flag after handling valid input
      backspacePressedRef.current = false;
    } else {
      // Handle backspace or empty input
      if (backspacePressedRef.current || inputValue === '') {
        // Handle backspace - clear current and move to previous
        const newValue = value.split('');

        newValue[index] = '';
        const updatedValue = newValue.join('');

        onChange(updatedValue);

        if (index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      } else {
        // Non-numeric character was typed - filter it out and maintain current value
        // Call onChange with current value to notify that input was filtered
        onChange(value);
      }

      // Reset backspace flag
      backspacePressedRef.current = false;
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      // Track that backspace was pressed
      backspacePressedRef.current = true;
      
      if (!value[index] && index > 0) {
        // If current cell is empty and backspace is pressed, move to previous cell
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, cellCount);
    
    if (pastedData.length > 0) {
      onChange(pastedData);
      
      // Focus the next empty cell or the last cell
      const nextIndex = Math.min(pastedData.length, cellCount - 1);

      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className={styles.codeField}>
      {Array.from({ length: cellCount }, (_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex(null)}
          disabled={disabled}
          className={`${styles.cell} ${
            focusedIndex === index ? styles.focused : ''
          } ${value[index] ? styles.filled : ''}`}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};
